import {
  Component, ChangeDetectionStrategy, Input, OnInit, inject, DestroyRef, ChangeDetectorRef,
} from '@angular/core'
import {
  AspectRatio, ColumnWidth, LooseAspectRatio, RequireOne, breakpointColumns,
} from '@knorr-bremse-portals/ngx-components'

import { BreakpointObserver } from '@angular/cdk/layout'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

export type SourceSet = { [width: string | '?']: string }

export type PictureSources = RequireOne<{
  [A in AspectRatio]: SourceSet
}>

export type PictureVariant = {
  aspectRatio: LooseAspectRatio
  mediaQuery?: string
}
export type PictureVariants = PictureVariant[]

function mapSizes(width: ColumnWidth): string {
  let sizes = ''
  for (const entry of Object.entries(breakpointColumns)) {
    if (entry[0] === 'default') {
      continue
    }
    sizes += `${entry[0]} ${Math.min(width / entry[1] * 100, 100)}vw,`
  }
  sizes += `${Math.min(width / breakpointColumns['default'] * 100, 100)}vw`
  return sizes
}

const sizes: { [columnWidth in ColumnWidth]: string } = {
  [ColumnWidth.Full]: mapSizes(ColumnWidth.Full),
  [ColumnWidth.Wider]: mapSizes(ColumnWidth.Wider),
  [ColumnWidth.Wide]: mapSizes(ColumnWidth.Wide),
  [ColumnWidth.Half]: mapSizes(ColumnWidth.Half),
  [ColumnWidth.Narrow]: mapSizes(ColumnWidth.Narrow),
  [ColumnWidth.OneFourth]: mapSizes(ColumnWidth.OneFourth),
  [ColumnWidth.OneSixth]: mapSizes(ColumnWidth.OneSixth),
  [ColumnWidth.OneTwelth]: mapSizes(ColumnWidth.OneTwelth),
}

@Component({
  selector: 'kb-picture',
  templateUrl: './picture.component.html',
  styleUrls: ['./picture.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
})
export class PictureComponent implements OnInit {
  @Input() public variants: PictureVariants = [{
    aspectRatio: AspectRatio.Standard,
  }]

  /** The number of columns the picture would stretch in the root grid, this does not need to be exact, it only optimizes the resolution of the picture, if unsure use a bigger value */
  @Input({ required: true }) public rootColumns!: ColumnWidth

  @Input({ required: true }) public picture!: PictureSources
  @Input({ required: true }) public alt!: string
  @Input() public draggable?: boolean
  @Input() public aboveFold?: boolean

  private breakpointObserver = inject(BreakpointObserver)
  private destroyRef = inject(DestroyRef)
  private cdr = inject(ChangeDetectorRef)

  protected currentVariant!: PictureVariant

  protected fallbackSource!: string

  protected sizes!: string

  public ngOnInit(): void {
    this.observeVariant()
    this.fallbackSource = this.getFallbackSource()
    this.sizes = sizes[this.rootColumns]
  }

  protected toSrcsetString(srcset: SourceSet): string {
    return Object.entries(srcset)
      .filter(([width]) => width !== '?')
      .map(([width, src]) => `${src} ${width}w`)
      .join(', ')
  }

  private observeVariant() {
    const mediaQueries = this.variants
      .filter(v => v.mediaQuery !== undefined)
      .map(v => v.mediaQuery as string)

    if (mediaQueries.length > 0) {
      this.breakpointObserver.observe(mediaQueries).pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(() => {
        this.currentVariant = this.getCurrentVariant()
        this.cdr.markForCheck()
      })
    }

    this.currentVariant = this.getCurrentVariant()
  }

  private getCurrentVariant(): PictureVariant {
    for (const variant of this.variants) {
      if (!variant.mediaQuery) {
        continue
      }
      if (!this.breakpointObserver.isMatched(variant.mediaQuery)) {
        continue
      }
      return variant
    }

    return this.getFallbackVariant()
  }

  private getFallbackVariant(): PictureVariant {
    // TODO: update this with BreakpointObserver
    return this.variants.find(variant => variant.mediaQuery === undefined) ?? this.variants[this.variants.length - 1]
  }

  private getFallbackSourceSet(): SourceSet {
    const fallbackAspectRatio = this.getFallbackVariant().aspectRatio
    return this.picture[fallbackAspectRatio] ?? Object.values(this.picture)[0]
  }

  private getFallbackSource(): string {
    const srcset = this.getFallbackSourceSet()
    const widths = Object.keys(srcset)
    const fallbackWidth = widths[widths.length - 1]
    return srcset[fallbackWidth]
  }
}
